import type {
  Payer as PayerRecord,
  RateRecord,
  ServiceCode as ServiceCodeRecord,
} from '@homecare/contracts';
import {
  Button,
  Card,
  EmptyState,
  Field,
  PageHeader,
  SelectField,
  Spinner,
  StatusPill,
} from '@homecare/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';
import { useHasPermission } from '../../lib/session';

/**
 * Rates — what each payer is billed, and what each caregiver is paid, per
 * service code (§6.3.3).
 *
 * `GET` and `POST /v1/rates` existed from slice 1 with nothing calling them:
 * the Rates item on the rail opened a placeholder, so the only way a price got
 * into the system was the seed. The reachability gate stopped mistaking the
 * page path '/rates' for a call in slice 2 session 9, which is what showed it.
 *
 * ## A price is never edited
 *
 * There is no PATCH on rates and this screen does not pretend otherwise.
 * "Change this price" inserts a new row from a date and end-dates the old one
 * to the day before, in one request (`supersedesRateId`) — so a charge already
 * computed from the old price still agrees with the row that produced it.
 *
 * Dates are shown as stored. Whether a row is in force *today* depends on the
 * agency's day, which the browser does not know; the dates say it exactly.
 */

const TYPE_LABEL = { BILL: 'Billed to payer', PAY: 'Paid to caregiver' } as const;
const DIFFERENTIAL_LABEL: Record<string, string> = {
  STANDARD: 'Standard',
  WEEKEND: 'Weekend',
  HOLIDAY: 'Holiday',
  OVERNIGHT: 'Overnight',
  LIVE_IN: 'Live-in',
  HOLIDAY_OVERTIME: 'Holiday overtime',
};

const dollars = (cents: string): string =>
  `$${(Number(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

/** "18.50" or "18" or "$18.50" → "1850"; null when it is not an amount. */
export function toCents(input: string): string | null {
  const match = /^\$?\s*(\d{1,6})(?:\.(\d{1,2}))?$/.exec(input.trim());
  if (!match) return null;
  return String(Number(match[1]) * 100 + Number((match[2] ?? '0').padEnd(2, '0')));
}

export function RatesPage(): ReactNode {
  const api = useApi();
  const canWrite = useHasPermission('rate:write');
  const [payerId, setPayerId] = useState('');
  const [rateType, setRateType] = useState('');
  const [editing, setEditing] = useState<RateRecord | 'new' | null>(null);

  const payers = useQuery({
    queryKey: ['payers'],
    queryFn: () => api.get<{ payers: PayerRecord[] }>('/v1/payers'),
  });
  const serviceCodes = useQuery({
    queryKey: ['service-codes'],
    queryFn: () => api.get<{ serviceCodes: ServiceCodeRecord[] }>('/v1/service-codes'),
  });

  const query = new URLSearchParams({
    ...(payerId ? { payerId } : {}),
    ...(rateType ? { rateType } : {}),
  }).toString();
  const rates = useQuery({
    queryKey: ['rates', payerId, rateType],
    queryFn: () => api.get<{ rates: RateRecord[] }>(`/v1/rates${query ? `?${query}` : ''}`),
  });

  const payerName = (id: string): string =>
    payers.data?.payers.find((payer) => payer.id === id)?.name ?? 'A payer';
  const code = (id: string): ServiceCodeRecord | undefined =>
    serviceCodes.data?.serviceCodes.find((entry) => entry.id === id);

  return (
    <div className="grid gap-5">
      <PageHeader
        title="Rates"
        meta="What each payer is billed and what caregivers are paid, per service code. A price is changed from a date, never rewritten."
        action={
          canWrite && editing === null ? (
            <Button onClick={() => setEditing('new')}>Add a rate</Button>
          ) : undefined
        }
      />

      {editing !== null && (
        <RateForm
          replacing={editing === 'new' ? null : editing}
          payers={payers.data?.payers ?? []}
          serviceCodes={serviceCodes.data?.serviceCodes ?? []}
          onDone={() => setEditing(null)}
        />
      )}

      <Card>
        <div className="grid gap-3 sm:grid-cols-2">
          <SelectField label="Payer" value={payerId} onChange={(e) => setPayerId(e.target.value)}>
            <option value="">Every payer</option>
            {(payers.data?.payers ?? []).map((payer) => (
              <option key={payer.id} value={payer.id}>
                {payer.name}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="Billed or paid"
            value={rateType}
            onChange={(e) => setRateType(e.target.value)}
          >
            <option value="">Both</option>
            <option value="BILL">Billed to payer</option>
            <option value="PAY">Paid to caregiver</option>
          </SelectField>
        </div>

        <ErrorNote error={rates.error ?? payers.error} nextStep="Reload the page to try again." />

        {rates.isPending ? (
          <p className="text-ink-2 mt-4 flex items-center gap-2 text-sm" role="status">
            <Spinner /> Loading rates…
          </p>
        ) : (rates.data?.rates ?? []).length === 0 ? (
          <EmptyState
            title="No rates"
            explanation="Without a rate a visit has no price and a caregiver has no pay for it. Add one per payer and service code."
          />
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-ink-3 border-line border-b text-xs uppercase tracking-wide">
                  <th className="py-2 pr-3">Service</th>
                  <th className="py-2 pr-3">Payer</th>
                  <th className="py-2 pr-3">Kind</th>
                  <th className="py-2 pr-3 text-right">Amount</th>
                  <th className="py-2 pr-3">From</th>
                  <th className="py-2 pr-3">Until</th>
                  {canWrite && <th className="py-2" />}
                </tr>
              </thead>
              <tbody>
                {rates.data!.rates.map((rate) => {
                  const service = code(rate.serviceCodeId);
                  return (
                    <tr key={rate.id} className="border-line border-b align-top">
                      <td className="py-2 pr-3">
                        <span className="text-ink font-medium">
                          {service?.code ?? 'A service code'}
                        </span>
                        {service?.description && (
                          <span className="text-ink-3 block text-xs">{service.description}</span>
                        )}
                        {rate.clientId && (
                          <span className="text-ink-3 block text-xs">One client only</span>
                        )}
                      </td>
                      <td className="text-ink-2 py-2 pr-3">{payerName(rate.payerId)}</td>
                      <td className="py-2 pr-3">
                        <StatusPill tone={rate.rateType === 'BILL' ? 'scheduled' : 'progress'}>
                          {TYPE_LABEL[rate.rateType]}
                        </StatusPill>
                        {rate.differential !== 'STANDARD' && (
                          <span className="text-ink-3 block text-xs">
                            {DIFFERENTIAL_LABEL[rate.differential] ?? rate.differential}
                          </span>
                        )}
                      </td>
                      <td className="text-ink py-2 pr-3 text-right font-medium" data-numeric>
                        {dollars(rate.amountCents)}
                      </td>
                      <td className="text-ink-2 py-2 pr-3" data-numeric>
                        {rate.effectiveFrom}
                      </td>
                      <td className="text-ink-2 py-2 pr-3" data-numeric>
                        {rate.effectiveTo ?? 'Open'}
                      </td>
                      {canWrite && (
                        <td className="py-2 text-right">
                          {rate.effectiveTo === null && (
                            <Button variant="ghost" size="sm" onClick={() => setEditing(rate)}>
                              Change this price
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function RateForm({
  replacing,
  payers,
  serviceCodes,
  onDone,
}: {
  replacing: RateRecord | null;
  payers: PayerRecord[];
  serviceCodes: ServiceCodeRecord[];
  onDone: () => void;
}): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();
  const [payerId, setPayerId] = useState(replacing?.payerId ?? '');
  const [serviceCodeId, setServiceCodeId] = useState(replacing?.serviceCodeId ?? '');
  const [rateType, setRateType] = useState<string>(replacing?.rateType ?? 'BILL');
  const [differential, setDifferential] = useState<string>(replacing?.differential ?? 'STANDARD');
  const [amount, setAmount] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [failure, setFailure] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const cents = toCents(amount);
  const blocked = !payerId
    ? 'Choose the payer.'
    : !serviceCodeId
      ? 'Choose the service code.'
      : cents === null
        ? 'Give the amount in dollars and cents, like 18.50.'
        : !effectiveFrom
          ? 'Give the first day it applies.'
          : null;

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (blocked) return;
    setBusy(true);
    setFailure(null);
    try {
      await api.send('POST', '/v1/rates', {
        payerId,
        serviceCodeId,
        clientId: replacing?.clientId ?? null,
        rateType,
        differential,
        amountCents: cents,
        minShiftMinutes: replacing?.minShiftMinutes ?? null,
        effectiveFrom,
        supersedesRateId: replacing?.id ?? null,
      });
      await queryClient.invalidateQueries({ queryKey: ['rates'] });
      onDone();
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <h2 className="text-ink text-lg font-bold">
        {replacing ? 'Change this price' : 'Add a rate'}
      </h2>
      {replacing && (
        <p className="text-ink-2 mt-1 text-sm">
          Now {dollars(replacing.amountCents)} from {replacing.effectiveFrom}. The new price starts
          on the day you give; the current one ends the day before. Nothing already charged changes.
        </p>
      )}
      <ErrorNote error={failure} nextStep="Check the entry and try again." />
      <form onSubmit={(event) => void submit(event)} className="mt-4 grid gap-3 sm:grid-cols-2">
        {!replacing && (
          <>
            <SelectField label="Payer" value={payerId} onChange={(e) => setPayerId(e.target.value)}>
              <option value="">Choose…</option>
              {payers.map((payer) => (
                <option key={payer.id} value={payer.id}>
                  {payer.name}
                </option>
              ))}
            </SelectField>
            <SelectField
              label="Service code"
              value={serviceCodeId}
              onChange={(e) => setServiceCodeId(e.target.value)}
            >
              <option value="">Choose…</option>
              {serviceCodes
                .filter((entry) => !payerId || entry.payerId === payerId)
                .map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.code}
                    {entry.description ? ` — ${entry.description}` : ''}
                  </option>
                ))}
            </SelectField>
            <SelectField
              label="Kind"
              value={rateType}
              onChange={(e) => setRateType(e.target.value)}
            >
              <option value="BILL">Billed to payer</option>
              <option value="PAY">Paid to caregiver</option>
            </SelectField>
            <SelectField
              label="When it applies"
              value={differential}
              onChange={(e) => setDifferential(e.target.value)}
            >
              {Object.entries(DIFFERENTIAL_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </SelectField>
          </>
        )}
        <Field
          label={replacing ? 'New amount' : 'Amount'}
          hint="Per unit of the service code, in dollars."
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <Field
          label="First day it applies"
          type="date"
          value={effectiveFrom}
          onChange={(e) => setEffectiveFrom(e.target.value)}
        />
        <div className="flex gap-2 sm:col-span-2">
          <Button
            type="submit"
            pending={busy}
            {...(blocked ? { disabled: true as const, disabledReason: blocked } : {})}
          >
            {replacing ? 'Save the new price' : 'Save the rate'}
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
